import type { AssetId, ConceptId, FilterGroup, FilterGroups, LabelId } from "@storytell/types";
import { createStore, produce } from "solid-js/store";
import type { Wire } from "~/wire/definition";
import { useWire } from "~/wire/WireProvider";

export type FilterOperator = "and" | "or";
export type UIFilterExpression<T> = {
  operator: FilterOperator;
  filter: (item: T) => boolean;
  kind: "asset" | "concept" | "label";
  id: AssetId | ConceptId | LabelId;
};
export type UIFilterGroup<T> = {
  operator: FilterOperator;
  filters: UIFilterExpression<T>[];
};
export type Filters<T> = UIFilterGroup<T>[];

export type FilterBuilder<Item> = ReturnType<typeof useFilterBuilder<Item>>;

/**
 * The UI and BE filters have a similar structure, with one key difference:
 * The UI considers the first operator extraneous, the BE considers the last operator extraneous.
 *
 * For example: [{id: 1, operator: "and"}, {id: 2, operator: "or"}];
 * This is a simplified version of a filter.
 * The UI will consider the `or` as the operator between the two filters, the BE will consider the `and` as the operator between the two filters.
 *
 * The UI structure makes it easier to both display and build the filtering logic,
 * so it's kept different and only converted when sending to the BE.
 */
export const uiFiltersToServerFilters = <Item>(filters: Filters<Item>): FilterGroups => {
  if (filters.length === 0 || filters[0]?.filters.length === 0) return [];

  const result: FilterGroups = [];
  filters.forEach((group, index) => {
    const g: FilterGroup = {
      group: [],
      operator: filters[index + 1]?.operator || "and",
    };

    group.filters.forEach((filter, filterIndex) => {
      g.group.push({
        id: filter.id as LabelId,
        kind: filter.kind as "label",
        operator: group.filters[filterIndex + 1]?.operator || "and",
      });
    });

    result.push(g);
  });

  return result;
};

export const serverFiltersToUiFilters = <Item>(wire: Wire, filters?: FilterGroups): Filters<Item> => {
  if (!filters) return [{ operator: "and", filters: [] }];

  const result: Filters<Item> = [];
  filters.forEach((group, index) => {
    const g: UIFilterGroup<Item> = {
      filters: [],
      operator: filters[index + 1]?.operator || "and",
    };

    group.group.forEach((filter, filterIndex) => {
      g.filters.push({
        id: filter.id as LabelId,
        kind: filter.kind as "label",
        operator: group.group[filterIndex - 1]?.operator || "and",
        filter: (entity: Item) => {
          if (typeof entity === "object" && entity && "id" in entity && typeof entity.id === "string") {
            if (entity.id.startsWith("asset_")) {
              return wire.sync.getLabelsForAsset(entity.id as AssetId).some((id) => id === filter.id);
            }
            if (entity.id.startsWith("concept_")) {
              return wire.sync.getLabelsForConcept(entity.id as ConceptId).some((id) => id === filter.id);
            }
          }
          return true;
        },
      });
    });

    result.push(g);
  });

  return result;
};

export function useFilterBuilder<Item>() {
  const [state, setState] = createStore<{ filter: Filters<Item> }>({
    filter: [{ operator: "and", filters: [] }],
  });

  const addFilterGroup = (op: FilterOperator = "and", filters: UIFilterExpression<Item>[] = []) => {
    setState("filter", (prev) => [...prev, { operator: op, filters }]);
    return state.filter[state.filter.length - 1]!;
  };

  const addFilter = (filter: UIFilterExpression<Item>, groupIndex: number) => {
    setState(
      produce((state) => {
        state.filter[groupIndex]?.filters.push(filter);
      }),
    );
    return filter;
  };

  const removeFilter = (filterId: string, groupIndex: number) => {
    setState(
      produce((state) => {
        if (!state.filter[groupIndex]) {
          return;
        }
        state.filter[groupIndex].filters = state.filter[groupIndex].filters.filter((f) => f.id !== filterId);
      }),
    );
  };

  const removeFilterGroup = (groupIndex: number) => {
    setState(
      produce((state) => {
        state.filter.splice(groupIndex, 1);
      }),
    );
  };

  const changeGroupOperator = (op: FilterOperator, groupIndex: number) => {
    setState(
      produce((state) => {
        if (!state.filter[groupIndex]) {
          return;
        }
        state.filter[groupIndex].operator = op;
      }),
    );
  };

  const changeFilterOperator = (filterId: string, op: FilterOperator, groupIndex: number) => {
    setState(
      produce((state) => {
        if (!state.filter[groupIndex]) {
          return;
        }
        state.filter[groupIndex].filters.find((f) => f.id === filterId)!.operator = op;
      }),
    );
  };

  const buildFilterGroupFunction = (group: UIFilterGroup<Item>) => {
    return (item: Item) => {
      if (group.filters.length === 0) return true;
      let result = group.filters[0].filter(item);

      group.filters.forEach((filter, i) => {
        if (i === 0) return;

        if (filter.operator === "and") {
          result = result && filter.filter(item);
        } else {
          result = result || filter.filter(item);
        }
      });

      return result;
    };
  };

  const buildFilterFunction = () => {
    return (item: Item) => {
      if (state.filter.length === 0) return true;

      let result = buildFilterGroupFunction(state.filter[0]!)(item);

      state.filter.forEach((group, i) => {
        if (i === 0) return;
        if (group.operator === "and") {
          result = result && buildFilterGroupFunction(group)(item);
        } else {
          result = result || buildFilterGroupFunction(group)(item);
        }
      });

      return result;
    };
  };

  const resetFilter = () => {
    setState({ filter: [{ operator: "and", filters: [] }] });
  };

  const setFilter = (filter: UIFilterExpression<Item>) => {
    setState({ filter: [{ operator: "and", filters: [filter] }] });
  };

  return {
    state,
    addFilterGroup,
    addFilter,
    removeFilter,
    removeFilterGroup,
    changeGroupOperator,
    changeFilterOperator,
    buildFilterFunction,
    resetFilter,
    setFilter,
    setState,
  };
}
